import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button as SubmitButton } from "@/components/ui/Button";
import CountryDropdown, {
  CountryDropdownOption,
} from "@/components/ui/CountryDropdown.web";
import CustomModal from "@/components/ui/CustomModal.web";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { useThemeColor } from "@/hooks/useThemeColor";
import { UserDocument } from "@/types/user";
import { getCroppedImage, PixelCrop } from "@/utils/cropImage.web";
import { isUsernameAvailable as checkUsernameAvailability } from "@/utils/username.web";
import { Feather } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import { getApp } from "firebase/app";
import { getAuth, updateProfile } from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { t } from "i18next";
import { VerifiedDisplayName } from "@/components/ui/VerifiedDisplayName";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import type { Area } from "react-easy-crop";
import { Controller, useForm } from "react-hook-form";
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import "./profile.css";

// @ts-ignore - image import
import defaultProfileImg from "@/assets/images/default-profile.webp";

// react-datepicker (+ its CSS) and react-easy-crop live behind React.lazy so
// their bytes don't ship on the initial page load. First open of either modal
// pays a one-time chunk fetch (see Suspense fallback below).
const ProfileDatePicker = React.lazy(
  () => import("@/components/profile/ProfileDatePicker.web"),
);
const ProfileAvatarCropper = React.lazy(
  () => import("@/components/profile/ProfileAvatarCropper.web"),
);

const firestore = getFirestore(getApp());
const storage = getStorage(getApp());

export default function ProfileScreen() {
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const [loading, setLoading] = useState(true);

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailabilityError, setUsernameAvailabilityError] =
    useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [country, setCountry] = useState("");
  const [bio, setBio] = useState("");
  const [fullNameSet, setFullNameSet] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Image cropping state
  const [cropImageUri, setCropImageUri] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(
    null,
  );

  // Modal states
  const [alertConfig, setAlertConfig] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({ visible: false, title: "", message: "" });

  const [confirmConfig, setConfirmConfig] = useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ visible: false, title: "", message: "", onConfirm: () => {} });

  const primaryColor = useThemeColor({}, "primary");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const dateOfBirthSaved = !!userData?.dateOfBirth;

  const thirteenYearsAgo = new Date(
    new Date().getFullYear() - 13,
    new Date().getMonth(),
    new Date().getDate(),
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");

  const isProfileChanged = () => {
    if (!userData) return false;
    const isChanged =
      username !== (userData.username || "") ||
      country !== (userData.country || "") ||
      bio !== (userData.bio || "");
    return isChanged;
  };

  const parseDateOfBirth = (dob: any): Date | null => {
    if (!dob) return null;
    if (dob instanceof Date) return dob;
    if (typeof dob.toDate === "function") return dob.toDate();
    if (typeof dob === "string") return new Date(dob);
    return null;
  };

  const formatDateOfBirth = (date: Date | null) => {
    if (!date) return "N/A";
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const userDocRef = doc(firestore, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const data = userDocSnap.data() as UserDocument;
            setUserData(data);
            setUsername(data.username || "");
            setDateOfBirth(parseDateOfBirth(data.dateOfBirth));
            setCountry(data.country || "");
            setBio(data.bio || "");
            setFullNameSet(data.fullNameSet || false);
            setValue("firstName", data.firstName || "");
            setValue("lastName", data.lastName || "");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [user, setValue]);

  const handleProfilePicChange = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      quality: 1,
    });

    if (!result.canceled) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropImageUri(result.assets[0].uri);
    }
  };

  const handleCropConfirm = async () => {
    if (!cropImageUri || !croppedAreaPixels || !user) return;
    setUploading(true);
    try {
      const blob = await getCroppedImage(cropImageUri, croppedAreaPixels);
      const refPath = `profile-pictures/${user.uid}/original.jpeg`;
      const imageRef = storageRef(storage, refPath);
      await uploadBytes(imageRef, blob);
      const url = await getDownloadURL(imageRef);
      const userDocRef = doc(firestore, "users", user.uid);
      await updateDoc(userDocRef, { photoURL: url });
      // Keep Firebase Auth in sync so denormalized writes (e.g. comment
      // author.photoURL = user.photoURL) pick up the new URL.
      const authUser = getAuth(getApp()).currentUser;
      if (authUser) {
        await updateProfile(authUser, { photoURL: url });
      }
      setUserData((prev) => (prev ? { ...prev, photoURL: url } : null));
      setCropImageUri(null);
    } catch (error) {
      console.error("Error uploading image: ", error);
      setAlertConfig({
        visible: true,
        title: t("ERROR"),
        message: t("FAILED_TO_UPLOAD_IMAGE"),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropImageUri(null);
    setCroppedAreaPixels(null);
  };

  const handleFullNameUpdate = async (data: {
    firstName: string;
    lastName: string;
  }) => {
    if (user) {
      setConfirmConfig({
        visible: true,
        title: t("CONFIRM_FULL_NAME_AND_DATE_OF_BIRTH"),
        message: t("CONFIRM_FULL_NAME_MESSAGE"),
        onConfirm: async () => {
          try {
            const userDocRef = doc(firestore, "users", user.uid);
            await updateDoc(userDocRef, {
              firstName: data.firstName,
              lastName: data.lastName,
              displayName: `${data.firstName} ${data.lastName}`,
              fullNameSet: true,
            });

            setUserData((prev) =>
              prev
                ? {
                    ...prev,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    displayName: `${data.firstName} ${data.lastName}`,
                    fullNameSet: true,
                  }
                : prev,
            );

            setFullNameSet(true);
            setAlertConfig({
              visible: true,
              title: t("FULL_NAME_SET"),
              message: t("FULL_NAME_MESSAGE_SET"),
            });
          } catch (error) {
            console.error("Error updating full name:", error);
            setAlertConfig({
              visible: true,
              title: t("ERROR"),
              message: t("FAILED_TO_UPDATE_PROFILE"),
            });
          }
        },
      });
    }
  };

  const handleDateOfBirthUpdate = async () => {
    if (!user) return;
    if (!dateOfBirth) {
      setAlertConfig({
        visible: true,
        title: t("ERROR"),
        message: t("PLEASE_SELECT_DATE_OF_BIRTH"),
      });
      return;
    }
    setConfirmConfig({
      visible: true,
      title: t("CONFIRM_DATE_OF_BIRTH"),
      message: t("CONFIRM_DATE_OF_BIRTH_MESSAGE"),
      onConfirm: async () => {
        try {
          const jsDate =
            dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
          const userDocRef = doc(firestore, "users", user.uid);
          const dobTimestamp = Timestamp.fromDate(jsDate);
          await updateDoc(userDocRef, {
            dateOfBirth: dobTimestamp,
          });

          setUserData((prev) =>
            prev ? { ...prev, dateOfBirth: dobTimestamp } : prev,
          );

          setAlertConfig({
            visible: true,
            title: t("DATE_OF_BIRTH_SET"),
            message: t("DATE_OF_BIRTH_MESSAGE_SET"),
          });
        } catch (error) {
          console.error("Error updating date of birth:", error);
          setAlertConfig({
            visible: true,
            title: t("ERROR"),
            message: t("FAILED_TO_UPDATE_PROFILE"),
          });
        }
      },
    });
  };

  const validateUsername = (text: string) => {
    if (text.trim() === "") {
      setUsernameError(t("USERNAME_REQUIRED"));
    } else if (/\s/.test(text)) {
      setUsernameError(t("USERNAME_CANNOT_CONTAIN_SPACES"));
    } else if (text.length > 0 && text.length < 3) {
      setUsernameError(t("USERNAME_TOO_SHORT"));
    } else if (text.length > 0 && !/^[a-zA-Z0-9_.]+$/.test(text)) {
      setUsernameError(t("USERNAME_INVALID_CHARACTERS"));
    } else if (text.length > 0 && !/^[a-zA-Z0-9]/.test(text)) {
      setUsernameError(t("USERNAME_INVALID_START"));
    } else {
      setUsernameError("");
    }
    setUsername(text);
  };

  const isUsernameAvailable = useCallback(
    async (candidateUsername: string) => {
      if (!user) return false;

      return checkUsernameAvailability(firestore, candidateUsername, user.uid);
    },
    [user],
  );

  useEffect(() => {
    if (!user || !userData) return;

    const normalizedUsername = username.trim();
    const currentUsername = (userData.username || "").trim();

    if (
      !normalizedUsername ||
      !!usernameError ||
      normalizedUsername === currentUsername
    ) {
      setUsernameAvailabilityError("");
      setIsCheckingUsername(false);
      return;
    }

    let cancelled = false;
    setIsCheckingUsername(true);

    const timeoutId = setTimeout(async () => {
      try {
        const available = await isUsernameAvailable(normalizedUsername);
        if (cancelled) return;

        setUsernameAvailabilityError(
          available ? "" : t("USERNAME_ALREADY_TAKEN"),
        );
      } catch (error) {
        console.error("Error checking username availability:", error);
        if (!cancelled) {
          setUsernameAvailabilityError("");
        }
      } finally {
        if (!cancelled) {
          setIsCheckingUsername(false);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [user, userData, username, usernameError, isUsernameAvailable]);

  const handleUpdate = async () => {
    if (user) {
      if (usernameError) {
        setAlertConfig({
          visible: true,
          title: t("ERROR"),
          message: usernameError,
        });
        return;
      }

      const normalizedUsername = username.trim();
      const currentUsername = (userData?.username || "").trim();

      if (normalizedUsername !== username) {
        setUsername(normalizedUsername);
      }

      if (normalizedUsername !== currentUsername) {
        try {
          setIsCheckingUsername(true);
          const available = await isUsernameAvailable(normalizedUsername);
          if (!available) {
            const unavailableMessage = t("USERNAME_ALREADY_TAKEN");
            setUsernameAvailabilityError(unavailableMessage);
            setAlertConfig({
              visible: true,
              title: t("ERROR"),
              message: unavailableMessage,
            });
            return;
          }
        } catch (error) {
          console.error("Error checking username availability:", error);
        } finally {
          setIsCheckingUsername(false);
        }
      }

      try {
        const userDocRef = doc(firestore, "users", user.uid);

        await updateDoc(userDocRef, {
          username: normalizedUsername,
          country,
          bio,
        });

        setUserData((prev) =>
          prev
            ? {
                ...prev,
                username: normalizedUsername,
                country,
                bio,
              }
            : prev,
        );
        setAlertConfig({
          visible: true,
          title: t("PROFILE_UPDATED"),
          message: t("YOUR_PROFILE_HAS_BEEN_UPDATED_SUCCESSFULLY"),
        });
      } catch (error) {
        console.error("Error updating user data:", error);
        setAlertConfig({
          visible: true,
          title: t("ERROR"),
          message: t("FAILED_TO_UPDATE_PROFILE"),
        });
      }
    }
  };

  return (
    <ThemedView style={{ flex: 1 }}>
      <Head>
        <title>{t("PAGE_TITLE_PROFILE")}</title>
      </Head>
      <StatusBar style="dark" animated={true} />
      <ScrollView>
        <ThemedView style={styles.container}>
          <ThemedView style={styles.header}>
            <ThemedView style={styles.profilePicContainer}>
              <TouchableOpacity
                onPress={handleProfilePicChange}
                disabled={uploading || loading}
                testID="profile-avatar-button"
              >
                <Image
                  source={
                    userData?.photoURL
                      ? { uri: userData.photoURL }
                      : defaultProfileImg
                  }
                  style={styles.profilePic}
                  accessibilityLabel={t("USER_AVATAR_ALT")}
                />
                <ThemedView
                  style={[
                    styles.cameraBadge,
                    { backgroundColor: primaryColor },
                  ]}
                >
                  <Feather name="camera" size={18} color="#fff" />
                </ThemedView>
                {uploading && (
                  <ThemedView style={styles.uploadingOverlay}>
                    <ActivityIndicator size="large" color="#fff" />
                  </ThemedView>
                )}
              </TouchableOpacity>
            </ThemedView>
            <VerifiedDisplayName
              user={userData}
              style={styles.name}
              loading={loading}
              badgeSize={22}
              containerStyle={{ justifyContent: "center" }}
            />
          </ThemedView>
          {!fullNameSet && (
            <ThemedView style={[styles.details, styles.fullNameForm]}>
              <ThemedText style={styles.label}>{t("FIRST_NAME")}:</ThemedText>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.input}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    editable={!fullNameSet}
                    testID="first-name-input"
                  />
                )}
                name="firstName"
              />
              {errors.firstName && (
                <ThemedText style={styles.errorText}>
                  {t("PLEASE_ENTER_FIRST_NAME")}
                </ThemedText>
              )}
              <ThemedText style={styles.label}>{t("LAST_NAME")}:</ThemedText>
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.input}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    editable={!fullNameSet}
                    testID="last-name-input"
                  />
                )}
                name="lastName"
              />
              {errors.lastName && (
                <ThemedText style={styles.errorText}>
                  {t("PLEASE_ENTER_LAST_NAME")}
                </ThemedText>
              )}

              <ThemedView style={styles.updateProfileButton}>
                <SubmitButton
                  text={t("SAVE_PERSONAL_DATA")}
                  onPress={handleSubmit(handleFullNameUpdate)}
                  disabled={fullNameSet || !firstName || !lastName}
                  testID="save-personal-data-button"
                />
              </ThemedView>
            </ThemedView>
          )}

          {!dateOfBirthSaved && (
            <ThemedView style={[styles.details, styles.fullNameForm]}>
              <ThemedText style={styles.label}>
                {t("DATE_OF_BIRTH")}:
              </ThemedText>
              <ThemedView style={styles.dateContainer}>
                <div className="custom-date-wrapper">
                  <Suspense
                    fallback={
                      <ActivityIndicator size="small" color={primaryColor} />
                    }
                  >
                    <ProfileDatePicker
                      selected={dateOfBirth}
                      onChange={(date) => setDateOfBirth(date)}
                      maxDate={thirteenYearsAgo}
                      openToDate={dateOfBirth || thirteenYearsAgo}
                    />
                  </Suspense>
                  <span className="custom-date-icon">
                    <Feather name="calendar" size={18} color="#555" />
                  </span>
                </div>
              </ThemedView>

              <ThemedView style={styles.updateProfileButton}>
                <SubmitButton
                  text={t("SAVE_DATE_OF_BIRTH")}
                  onPress={handleDateOfBirthUpdate}
                  style={{ maxWidth: "100%" }}
                  disabled={!dateOfBirth}
                  testID="save-dob-button"
                />
              </ThemedView>
            </ThemedView>
          )}

          {dateOfBirthSaved && (
            <ThemedView style={styles.details}>
              <ThemedText style={styles.labelRow}>
                <ThemedText style={styles.label}>
                  {t("DATE_OF_BIRTH")}:{" "}
                </ThemedText>
                <ThemedText style={styles.labelValue}>
                  {formatDateOfBirth(dateOfBirth)}
                </ThemedText>
              </ThemedText>
            </ThemedView>
          )}
          <ThemedView style={styles.details}>
            <ThemedText style={styles.label}>{t("USERNAME")}:</ThemedText>
            <TextInput
              style={styles.input}
              value={username}
              maxLength={20}
              onChangeText={validateUsername}
              testID="username-input"
            />
            {usernameError ? (
              <ThemedText style={styles.errorText}>{usernameError}</ThemedText>
            ) : usernameAvailabilityError ? (
              <ThemedText style={styles.errorText}>
                {usernameAvailabilityError}
              </ThemedText>
            ) : isCheckingUsername ? (
              <ThemedText style={styles.usernameStatusText}>
                {t("CHECKING_USERNAME_AVAILABILITY")}
              </ThemedText>
            ) : null}
            <ThemedText style={styles.label}>{t("COUNTRY")}:</ThemedText>
            <ThemedView style={styles.pickerContainer}>
              <CountryDropdown
                value={country}
                onChange={setCountry}
                placeholder={t("SELECT_COUNTRY")}
                options={COUNTRIES.map(
                  (c): CountryDropdownOption => ({
                    value: c.value,
                    label: c.label,
                    iso: c.iso,
                  }),
                )}
              />
            </ThemedView>

            <ThemedText style={styles.label}>{t("BIO")}:</ThemedText>
            <TextInput
              style={[styles.input, styles.bioInput]}
              value={bio}
              onChangeText={setBio}
              multiline
              testID="bio-input"
            />
            <ThemedView style={styles.updateProfileButton}>
              <SubmitButton
                text={t("UPDATE_PROFILE")}
                onPress={handleUpdate}
                style={{ maxWidth: "100%" }}
                disabled={
                  !isProfileChanged() ||
                  !!usernameError ||
                  !!usernameAvailabilityError ||
                  isCheckingUsername
                }
                testID="update-profile-button"
              />
            </ThemedView>
          </ThemedView>
        </ThemedView>
      </ScrollView>

      {/* Alert Modal */}
      <CustomModal
        visible={alertConfig.visible}
        onClose={() => setAlertConfig((prev) => ({ ...prev, visible: false }))}
        title={alertConfig.title}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ThemedText>{alertConfig.message}</ThemedText>
          <SubmitButton
            text="OK"
            onPress={() =>
              setAlertConfig((prev) => ({ ...prev, visible: false }))
            }
            testID="alert-modal-primary"
          />
        </div>
      </CustomModal>

      {/* Confirm Modal */}
      <CustomModal
        visible={confirmConfig.visible}
        onClose={() =>
          setConfirmConfig((prev) => ({ ...prev, visible: false }))
        }
        title={confirmConfig.title}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ThemedText>{confirmConfig.message}</ThemedText>
          <div style={{ display: "flex", gap: 10 }}>
            <SubmitButton
              text={t("CANCEL")}
              onPress={() =>
                setConfirmConfig((prev) => ({ ...prev, visible: false }))
              }
              style={{ flex: 1, backgroundColor: "#ccc" }}
              testID="confirm-modal-secondary"
            />
            <SubmitButton
              text="OK"
              onPress={() => {
                confirmConfig.onConfirm();
                setConfirmConfig((prev) => ({ ...prev, visible: false }));
              }}
              style={{ flex: 1 }}
              testID="confirm-modal-primary"
            />
          </div>
        </div>
      </CustomModal>

      {/* Crop Profile Picture Modal */}
      <CustomModal
        visible={!!cropImageUri}
        onClose={handleCropCancel}
        title={t("CROP_PROFILE_PICTURE")}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 320,
              background: "#222",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {cropImageUri && (
              <Suspense
                fallback={
                  <ActivityIndicator size="large" color={primaryColor} />
                }
              >
                <ProfileAvatarCropper
                  image={cropImageUri}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_: Area, areaPixels: Area) =>
                    setCroppedAreaPixels(areaPixels)
                  }
                />
              </Suspense>
            )}
          </div>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
          />
          <div style={{ display: "flex", gap: 12 }}>
            <SubmitButton
              text={t("CANCEL")}
              onPress={handleCropCancel}
              style={{ flex: 1, backgroundColor: "#ccc" }}
              testID="avatar-crop-cancel"
            />
            <SubmitButton
              text={uploading ? "..." : t("CONFIRM")}
              onPress={handleCropConfirm}
              disabled={!croppedAreaPixels || uploading}
              style={{ flex: 1 }}
              testID="avatar-crop-confirm"
            />
          </div>
        </div>
      </CustomModal>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },
  header: {
    marginTop: 20,
    alignItems: "center",
    marginBottom: 20,
  },
  profilePicContainer: {
    position: "relative",
    marginBottom: 10,
  },
  profilePic: {
    width: 150,
    height: 150,
    borderRadius: 75,
  },
  cameraBadge: {
    position: "absolute",
    bottom: 4,
    right: 4,
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 75,
  },
  name: {
    fontSize: 24,
    fontWeight: "bold",
  },
  details: {
    width: "100%",
    maxWidth: 500,
    alignSelf: "center",
    marginBottom: 20,
  },
  fullNameForm: {
    maxWidth: 500,
    width: "100%",
    alignSelf: "center",
    overflow: "visible",
    zIndex: 10,
  },
  label: {
    fontSize: 16,
    marginTop: 10,
    fontWeight: "bold",
  },
  labelRow: {
    fontSize: 16,
    marginTop: 10,
  },
  labelValue: {
    fontWeight: "normal",
  },
  input: {
    fontSize: 16,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    padding: 10,
    marginTop: 5,
    backgroundColor: "white",
    color: "#000",
  },
  bioInput: {
    height: 100,
    textAlignVertical: "top",
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    marginTop: 5,
    backgroundColor: "white",
    justifyContent: "center",
  },
  dateContainer: {
    marginTop: 5,
    zIndex: 1000,
    position: "relative",
  },
  updateProfileButton: {
    marginTop: 20,
    zIndex: 1,
    alignItems: "stretch",
  },
  errorText: {
    color: "red",
    fontSize: 14,
  },
  usernameStatusText: {
    fontSize: 14,
    marginTop: 4,
  },
});
